/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useExternalSubmissionThemeState } from '../../context/ExternalSubmissionThemeContext';
import {
	useGetFeedbackForm,
	useSaveFeedbackDraft,
	useSubmitFeedback,
} from '../../services/externalSubmissionQueries';
import { hideErrorValidationOnSave, validateForm } from '../../util/validateForm';
import FakeForm from '../FakeForm';
import NewUI from './NewUI';

const FeedbackForm = () => {
	const { t } = useTranslation('biz-collab-alt-feedback-spa');

	const { theme } = useExternalSubmissionThemeState();

	const [responses, setResponses] = useState([]);
	const [requiredQuestions, setRequiredQuestions] = useState([]);

	const { mutate: submitFeedback, isSuccess: isSubmitSuccess } = useSubmitFeedback();
	const { mutate: saveFeedbackDraft } = useSaveFeedbackDraft();

	const { data = {}, isLoading: isFormLoading, isError: isFormError } = useGetFeedbackForm();

	const { formCard, feedbackRequestId, feedbackRequestStatus, initialResponses = [] } = data;
	const [isDirty, setIsDirty] = useState(false);

	const isAdaptiveFormCard = formCard && formCard.type === 'AdaptiveCard';

	const handleBeforeUnload = useCallback((event) => {
		event.preventDefault();
		event.returnValue = '';
	}, []);

	useEffect(() => {
		if (initialResponses.length > 0) {
			setResponses(initialResponses);
		}
	}, [initialResponses]);

	useEffect(() => {
		if (!isDirty) {
			return;
		}
		window.addEventListener('beforeunload', handleBeforeUnload);

		// eslint-disable-next-line consistent-return
		return () => {
			window.removeEventListener('beforeunload', handleBeforeUnload);
		};
	}, [isDirty, handleBeforeUnload]);

	// The responses saved in the state are in the form of an array of objects with id and value, convert it to an object with id as key
	const convertResponsesToPayload = () => {
		const res = {};
		responses.forEach((response) => {
			res[response.id] = response.value;
		});

		return res;
	};

	const handleOnSaveButtonClick = () => {
		hideErrorValidationOnSave(requiredQuestions);
		const res = convertResponsesToPayload();
		const feedbackData = {
			feedbackRequestId,
			feedbackState: 'IN_PROGRESS',
			...res,
		};
		saveFeedbackDraft(feedbackData);
		setIsDirty(false);
	};

	const handleOnSubmitButtonClick = () => {
		if (validateForm(requiredQuestions, responses, t('required'))) {
			const res = convertResponsesToPayload();
			const feedbackData = {
				feedbackRequestId,
				feedbackState: 'SUBMITTED',
				...res,
			};

			submitFeedback(feedbackData);
			setIsDirty(false);
		}
	};

	const handleChange = useCallback(() => {
		setIsDirty((prev) => prev || true);
	}, []);

	// Show a Skeleton while the form is loading
	if (!isFormError && isFormLoading && !isAdaptiveFormCard) {
		return <FakeForm />;
	}

	// If the FeedbackRequestStatus is CANCELLED, don't show the feedback form.
	if (feedbackRequestStatus === 'CANCELLED') {
		return <div data-testid="feedbackForm-empty" />;
	}

	// Show the form if it has loaded and has not been submitted yet
	if (isAdaptiveFormCard && !isSubmitSuccess) {
		return (
			<NewUI
				data={data}
				responses={responses}
				setRequiredQuestions={setRequiredQuestions}
				setResponses={setResponses}
				handleOnSaveButtonClick={handleOnSaveButtonClick}
				handleOnSubmitButtonClick={handleOnSubmitButtonClick}
				theme={theme}
				handleChange={handleChange}
			/>
		);
	}

	return null;
};

export default FeedbackForm;
